import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { GbDashboardAuthService } from 'libs/gb-dashboard/account/utils/src/lib/gb-dashboard-auth-service/gb-dashboard-auth.service'
import { HelpConstants } from 'libs/gb-dashboard/help/feature/src/lib/help.constants'
import { combineLatest, filter, distinctUntilChanged, map } from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { AiFacade } from 'libs/gb-dashboard/shared/data-access-ai/src/lib/+state/ai.facade'
import * as AiActions from 'libs/gb-dashboard/shared/data-access-ai/src/lib/+state/ai.actions'
import {
  AutoFillAssistantInputItem,
  AutoFillFormTypeEnum,
  AutoFillSseEvent,
  AutoFillSseEventTypeEnum,
  GeneratedImageToolResult,
  SetupGuideAiModeEnum,
} from 'libs/gb-dashboard/shared/data-access-ai/src/lib/+state/ai.models'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { GbSetupGuideProgressService } from 'libs/gb-dashboard/setup-guide/utils/src/lib/gb-setup-guide-progress.service'
import { GbLocalStorageService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-localstorage-service/gb-localstorage.service'
import { GbIndexedDbService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-indexed-db-service/gb-indexed-db.service'
import {
  SetupGuideAiMessage,
  SetupGuideAiMessageType,
  SetupGuideAiMessageTypeEnum,
  SetupGuideAiChatSession,
  ToolDisplayType,
  ToolConfig,
  BrandingColorPreviewEntry,
  ArticleSearchResultEntry,
} from 'libs/gb-dashboard/setup-guide/utils/src/lib/ai-chat/types/ai-chat.types'
import {
  ToolRegistry,
  createToolRegistry,
} from 'libs/gb-dashboard/setup-guide/utils/src/lib/ai-chat/tools/tool-registry'
import { ToolApplyContext } from 'libs/gb-dashboard/setup-guide/utils/src/lib/ai-chat/tools/tool-handler.interface'
import { AiMarkdownUtil } from 'libs/gb-dashboard/setup-guide/utils/src/lib/ai-chat/utils/ai-markdown.util'
import { AiChatSessionUtil } from 'libs/gb-dashboard/setup-guide/utils/src/lib/ai-chat/utils/ai-chat-session.util'
import {
  buildAutoFillMessagesHistory,
  generateChatMessageId,
} from 'libs/gb-dashboard/setup-guide/utils/src/lib/ai-chat/utils/ai-message-history.util'

@UntilDestroy()
@Component({
  selector: 'gb-setup-guide-ai-tab',
  standalone: false,
  templateUrl: './gb-setup-guide-ai-tab.component.html',
  styleUrl: './gb-setup-guide-ai-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GbSetupGuideAiTabComponent implements OnInit, AfterViewInit {
  @Input() autoFillFormType: AutoFillFormTypeEnum | null | undefined
  @Input() aiPromptPlaceholder = ''
  private _shouldShowSetupWithAI = false
  @Input() set shouldShowSetupWithAI(value: boolean) {
    this._shouldShowSetupWithAI = value
    if (!value && this.aiMode === SetupGuideAiModeEnum.Act) {
      this.aiMode = SetupGuideAiModeEnum.Ask
    }
  }
  get shouldShowSetupWithAI(): boolean {
    return this._shouldShowSetupWithAI
  }

  @ViewChild('aiPromptInput') aiPromptInputRef?: ElementRef<HTMLTextAreaElement>
  @ViewChild('aiTabPanel') aiTabPanelRef?: ElementRef<HTMLDivElement>
  @ViewChild('aiGenerateButton') aiGenerateButtonRef?: ElementRef<HTMLButtonElement>
  @ViewChild('aiConversationTimeline') aiConversationTimelineRef?: ElementRef<HTMLDivElement>

  aiPromptControl = new FormControl('')
  editContentControl = new FormControl('')
  autoFillFormLoaded$ = this.aiFacade.autoFillFormLoaded$
  galabidHelpLoaded$ = this.aiFacade.galabidHelpLoaded$
  isAiBusy$ = combineLatest([this.autoFillFormLoaded$, this.galabidHelpLoaded$]).pipe(
    map(([autoFillFormLoaded, galabidHelpLoaded]) => !autoFillFormLoaded || !galabidHelpLoaded),
    distinctUntilChanged(),
  )
  protected readonly SetupGuideAiModeEnum = SetupGuideAiModeEnum
  protected readonly SetupGuideAiMessageTypeEnum = SetupGuideAiMessageTypeEnum
  private _aiMode: SetupGuideAiModeEnum = SetupGuideAiModeEnum.Ask
  get aiMode(): SetupGuideAiModeEnum {
    return this._aiMode
  }
  set aiMode(value: SetupGuideAiModeEnum) {
    this._aiMode = value
    this.selectedToolConfig = null
  }
  get aiModeOptions(): SetupGuideAiModeEnum[] {
    return [SetupGuideAiModeEnum.Ask, SetupGuideAiModeEnum.Act]
  }

  isAiModeDisabled(mode: SetupGuideAiModeEnum): boolean {
    if (mode === SetupGuideAiModeEnum.Act && !this.shouldShowSetupWithAI) {
      return true
    }
    if (this.aiChatMessages.length > 0 && mode !== this.aiMode) {
      return true
    }
    return false
  }
  aiChatMessages: SetupGuideAiMessage[] = []
  editingMessageId: string | null = null
  selectedToolConfig: ToolConfig | null = null
  private readonly actIntentKeywordsRegex = /\b(generate|create|design|build|customi[sz]e)\b/i

  get filteredAiChatSessions(): SetupGuideAiChatSession[] {
    return this.aiChatSessionUtil.filteredSessions
  }

  get selectedAiChatSessionId(): string | null {
    return this.aiChatSessionUtil.selectedSessionId
  }

  get currentSessionTitle(): string {
    const sessionId = this.aiChatSessionUtil.selectedSessionId
    if (!sessionId) {
      return ''
    }
    const session = this.filteredAiChatSessions.find((s) => s.id === sessionId)
    return session?.title ?? ''
  }

  get currentDashboardPage(): string | undefined {
    if (this.autoFillFormType === AutoFillFormTypeEnum.BrandingSettings) {
      return 'branding'
    }
    return undefined
  }

  get availableToolConfigs(): ToolConfig[] {
    return this.toolRegistry.getAllToolConfigs(this.currentDashboardPage)
  }

  get hasGeneratedImage(): boolean {
    return this.toolRegistry.hasGeneratedImage(this.aiChatMessages)
  }

  isStaff = this.gbDashboardAuthService.isStaff()
  isStaffSuperUserDistributor = this.gbDashboardAuthService.isStaffSuperUserDistributor()

  private toolRegistry: ToolRegistry = createToolRegistry(this.isStaffSuperUserDistributor)
  private aiMarkdownUtil: AiMarkdownUtil
  private aiChatSessionUtil: AiChatSessionUtil
  private processedStreamEventCount = 0

  constructor(
    private aiFacade: AiFacade,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private gbSetupGuideProgressService: GbSetupGuideProgressService,
    private gbLocalStorageService: GbLocalStorageService,
    private gbIndexedDbService: GbIndexedDbService,
    private changeDetectorRef: ChangeDetectorRef,
    private domSanitizer: DomSanitizer,
    private router: Router,
    private translateService: TranslateService,
    private gbDashboardAuthService: GbDashboardAuthService,
  ) {
    this.aiMarkdownUtil = new AiMarkdownUtil(this.domSanitizer, this.router)
    this.aiChatSessionUtil = new AiChatSessionUtil(this.gbLocalStorageService)
  }

  ngOnInit(): void {
    this.subscribeToAutoFillFormStreamEvents()
    this.subscribeToGalabidHelpStreamEvents()
    this.subscribeToAiBusyTransitions()
    this.loadAiChatHistory()
    this.aiPromptControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.adjustAiPromptHeight())
  }

  ngAfterViewInit(): void {
    this.adjustAiPromptHeight()
  }

  private adjustAiPromptHeight(): void {
    const textarea = this.aiPromptInputRef?.nativeElement
    const tabPanel = this.aiTabPanelRef?.nativeElement
    const generateButton = this.aiGenerateButtonRef?.nativeElement
    if (!textarea || !tabPanel) return

    const lineHeight = parseFloat(window.getComputedStyle(textarea).lineHeight) || 24
    const minHeight = lineHeight * 2
    const buttonHeight = generateButton?.offsetHeight ?? 0
    const availableHeight = Math.max(0, tabPanel.clientHeight - buttonHeight)
    const maxHeight = Math.max(minHeight, availableHeight * 0.5)

    textarea.style.height = `${minHeight}px`
    const contentHeight = Math.max(minHeight, textarea.scrollHeight)
    const nextHeight = Math.min(contentHeight, maxHeight)

    textarea.style.height = `${nextHeight}px`
    textarea.style.maxHeight = `${maxHeight}px`
    textarea.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden'
  }

  private resetAiPromptInput(): void {
    this.aiPromptControl.reset('')
    requestAnimationFrame(() => this.adjustAiPromptHeight())
  }

  private loadAiChatHistory(): void {
    this.selectedAuctionFacade.urlName$
      .pipe(
        untilDestroyed(this),
        filter((urlName): urlName is string => !!urlName),
        distinctUntilChanged(),
      )
      .subscribe((urlName) => {
        this.aiChatSessionUtil.loadSessionsForAuction(urlName)
        this.aiChatSessionUtil.setSearchValue(null)
        this.aiChatMessages = []
        this.changeDetectorRef.markForCheck()
      })
  }

  private subscribeToAutoFillFormStreamEvents(): void {
    this.aiFacade.autoFillFormStreamEvents$.pipe(untilDestroyed(this)).subscribe((events) => {
      if (events.length < this.processedStreamEventCount) {
        this.processedStreamEventCount = 0
      }

      const incomingEvents = events.slice(this.processedStreamEventCount)
      if (!incomingEvents.length) {
        return
      }

      incomingEvents.forEach((event) => this.appendEventMessage(event))
      this.processedStreamEventCount = events.length
      this.changeDetectorRef.markForCheck()
      this.scrollAiConversationToBottom()
    })
  }

  private subscribeToGalabidHelpStreamEvents(): void {
    this.aiFacade.galabidHelpStreamEvents$.pipe(untilDestroyed(this)).subscribe((events) => {
      if (events.length < this.processedStreamEventCount) {
        this.processedStreamEventCount = 0
      }

      const incomingEvents = events.slice(this.processedStreamEventCount)
      if (!incomingEvents.length) {
        return
      }

      incomingEvents.forEach((event) => this.appendEventMessage(event))
      this.processedStreamEventCount = events.length
      this.changeDetectorRef.markForCheck()
      this.scrollAiConversationToBottom()
    })
  }

  private subscribeToAiBusyTransitions(): void {
    this.isAiBusy$
      .pipe(
        distinctUntilChanged(),
        filter((isBusy) => !isBusy),
        untilDestroyed(this),
      )
      .subscribe(() => this.scrollAiConversationToBottom())
  }

  private appendEventMessage(event: AutoFillSseEvent): void {
    const now = new Date().toISOString()

    switch (event.type) {
      case AutoFillSseEventTypeEnum.ChatMessage:
        this.appendAiChatMessage({
          id: generateChatMessageId(),
          type: SetupGuideAiMessageTypeEnum.Assistant,
          side: 'left',
          content: event.payload.content,
          createdAt: now,
        })
        break
      case AutoFillSseEventTypeEnum.ToolCall: {
        const toolCallName = event.payload.tool
        this.appendAiChatMessage({
          id: generateChatMessageId(),
          type: SetupGuideAiMessageTypeEnum.ToolCall,
          side: 'left',
          content: this.toolRegistry.getGeneratingMessage(toolCallName),
          toolName: toolCallName,
          callId: event.payload.call_id,
          toolArguments: event.payload.arguments,
          showLoadingDots: true,
          createdAt: now,
        })
        break
      }
      case AutoFillSseEventTypeEnum.ToolResult: {
        const toolResultName = event.payload.tool
        this.completeToolCallLoading(toolResultName, event.payload.call_id)
        const toolResultMessage: SetupGuideAiMessage = {
          id: generateChatMessageId(),
          type: SetupGuideAiMessageTypeEnum.ToolResult,
          side: 'left',
          content: this.toolRegistry.getResultMessage(toolResultName),
          toolName: toolResultName,
          callId: event.payload.call_id,
          toolContent: event.payload.content,
          createdAt: now,
        }
        this.processImageToolResult(toolResultMessage)
        this.appendAiChatMessage(toolResultMessage)
        break
      }
      case AutoFillSseEventTypeEnum.Error:
        this.completeAllToolCallLoading()
        this.appendAiChatMessage({
          id: generateChatMessageId(),
          type: SetupGuideAiMessageTypeEnum.Error,
          side: 'left',
          content: event.payload.message,
          createdAt: now,
        })
        break
    }
  }

  private appendAiChatMessage(message: SetupGuideAiMessage): void {
    this.aiChatMessages = [...this.aiChatMessages, message]
    this.updateCurrentAiChatSessionMessages()
    this.changeDetectorRef.markForCheck()
    this.scrollAiConversationToBottom()
  }

  private isImageToolResult(message: SetupGuideAiMessage): boolean {
    return (
      message.type === SetupGuideAiMessageTypeEnum.ToolResult &&
      (message.toolName === 'generate_logo' || message.toolName === 'generate_banner')
    )
  }

  private processImageToolResult(message: SetupGuideAiMessage): void {
    if (!this.isImageToolResult(message) || !message.toolContent) {
      return
    }
    const imageResult = message.toolContent as GeneratedImageToolResult
    if (!imageResult.base64_image) {
      return
    }
    const imageKey = message.id
    const blob = this.base64ToBlob(imageResult.base64_image, 'image/png')
    const objectUrl = URL.createObjectURL(blob)
    this.toolRegistry.imageCache.setImageUrl(imageKey, objectUrl)
    message.toolContent = { ...imageResult, imageKey }
    this.gbIndexedDbService.storeImageBlob(imageKey, blob)
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteCharacters = atob(base64)
    const byteArrays: Uint8Array[] = []
    const sliceSize = 512
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)
      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }
      byteArrays.push(new Uint8Array(byteNumbers))
    }
    return new Blob(byteArrays as BlobPart[], { type: mimeType })
  }

  private async preloadSessionImages(messages: SetupGuideAiMessage[]): Promise<void> {
    const imageMessages = messages.filter((msg) => this.isImageToolResult(msg) && msg.toolContent)
    await Promise.all(
      imageMessages.map(async (msg) => {
        const imageResult = msg.toolContent as GeneratedImageToolResult
        if (!imageResult.imageKey || this.toolRegistry.imageCache.hasImageUrl(imageResult.imageKey)) {
          return
        }
        const blob = await this.gbIndexedDbService.getImageBlob(imageResult.imageKey)
        if (blob) {
          const objectUrl = URL.createObjectURL(blob)
          this.toolRegistry.imageCache.setImageUrl(imageResult.imageKey, objectUrl)
        }
      }),
    )
  }

  private scrollAiConversationToBottom(): void {
    this.changeDetectorRef.detectChanges()
    requestAnimationFrame(() => {
      const conversationShell = this.aiConversationTimelineRef?.nativeElement.parentElement
      if (conversationShell) {
        conversationShell.scrollTop = conversationShell.scrollHeight
      }
    })
  }

  private completeToolCallLoading(toolName: string, callId?: string): void {
    let hasUpdatedToolCall = false

    this.aiChatMessages = this.aiChatMessages.map((message) => {
      const toolCallMatches = callId ? message.callId === callId : message.toolName === toolName

      if (
        !hasUpdatedToolCall &&
        message.type === SetupGuideAiMessageTypeEnum.ToolCall &&
        toolCallMatches &&
        message.showLoadingDots
      ) {
        hasUpdatedToolCall = true
        return { ...message, showLoadingDots: false }
      }

      return message
    })
  }

  private completeAllToolCallLoading(): void {
    this.aiChatMessages = this.aiChatMessages.map((message) => {
      if (message.type === SetupGuideAiMessageTypeEnum.ToolCall && message.showLoadingDots) {
        return { ...message, showLoadingDots: false }
      }

      return message
    })
  }

  private updateCurrentAiChatSessionMessages(): void {
    this.aiChatSessionUtil.updateCurrentSessionMessages(this.aiChatMessages)
    this.changeDetectorRef.markForCheck()
  }

  private ensureAiChatSession(formType: AutoFillFormTypeEnum | null | undefined, prompt: string): void {
    this.aiChatSessionUtil.ensureSession(formType, prompt, this.aiMode)
  }

  private getAutoFillMessagesHistory(
    formType: AutoFillFormTypeEnum | null | undefined,
  ): AutoFillAssistantInputItem[] | undefined {
    return buildAutoFillMessagesHistory(this.aiChatMessages, formType)
  }

  renderMarkdown(content: string): SafeHtml {
    return this.aiMarkdownUtil.renderMarkdown(content, this.aiChatSessionUtil.currentAuctionUrlName)
  }

  onMarkdownLinkClick(event: MouseEvent): void {
    const targetElement = event.target
    if (targetElement instanceof Element) {
      const anchor = targetElement.closest('a')
      if (anchor instanceof HTMLAnchorElement && anchor.getAttribute('href') === '#galabid-support') {
        event.preventDefault()
        if (this.isStaff) {
          this.router.navigateByUrl(`/${HelpConstants.HELP}`)
        } else {
          this.gbSetupGuideProgressService.requestLiveChatMaximize()
        }
        return
      }
    }
    this.aiMarkdownUtil.onMarkdownLinkClick(event, this.aiChatSessionUtil.currentAuctionUrlName)
  }

  getMessageIcon(messageType: SetupGuideAiMessageType): string {
    switch (messageType) {
      case SetupGuideAiMessageTypeEnum.User:
        return 'person'
      case SetupGuideAiMessageTypeEnum.Assistant:
        return 'smart_toy'
      case SetupGuideAiMessageTypeEnum.ToolCall:
        return 'build_circle'
      case SetupGuideAiMessageTypeEnum.ToolResult:
        return 'auto_awesome'
      case SetupGuideAiMessageTypeEnum.Error:
        return 'error'
      default:
        return 'chat'
    }
  }

  startNewAiChatSession(): void {
    this.aiChatSessionUtil.startNewSession()
    this.aiChatMessages = []
    this.selectedToolConfig = null
    this.changeDetectorRef.markForCheck()
  }

  loadAiChatSession(sessionId: string): void {
    const messages = this.aiChatSessionUtil.loadSession(sessionId)
    if (messages) {
      this.aiChatMessages = messages
      this.selectedToolConfig = null
      this.preloadSessionImages(messages).then(() => {
        this.changeDetectorRef.markForCheck()
      })
      const sessionAiMode = this.aiChatSessionUtil.getCurrentSessionAiMode()
      if (sessionAiMode) {
        this.aiMode = sessionAiMode
      } else {
        const availableToolNames = new Set(this.toolRegistry.getAvailableToolNames(this.currentDashboardPage))
        const hasActToolCall = messages.some(
          (msg) =>
            (msg.type === SetupGuideAiMessageTypeEnum.ToolCall ||
              msg.type === SetupGuideAiMessageTypeEnum.ToolResult) &&
            !!msg.toolName &&
            availableToolNames.has(msg.toolName),
        )
        const inferredMode =
          hasActToolCall && this.shouldShowSetupWithAI ? SetupGuideAiModeEnum.Act : SetupGuideAiModeEnum.Ask
        this.aiMode = inferredMode
        this.aiChatSessionUtil.updateCurrentSessionAiMode(inferredMode)
      }
      this.changeDetectorRef.markForCheck()
    }
  }

  onAiChatSessionSelectionChange(event: Event): void {
    const selectedSessionId = (event.target as HTMLSelectElement | null)?.value || ''
    if (!selectedSessionId) {
      return
    }

    this.loadAiChatSession(selectedSessionId)
  }

  onLoadSessionFromMenu(sessionId: string): void {
    if (!sessionId) {
      return
    }
    this.loadAiChatSession(sessionId)
  }

  getToolDisplayType(message: SetupGuideAiMessage): ToolDisplayType {
    if (message.type !== SetupGuideAiMessageTypeEnum.ToolResult || !message.toolName) {
      return 'key-value'
    }
    return this.toolRegistry.getDisplayType(message.toolName)
  }

  getToolResultPreviewLines(message: SetupGuideAiMessage): string[] {
    if (!message.toolName) {
      return []
    }
    const previewData = this.toolRegistry.getPreviewData(message.toolName, message)
    return Array.isArray(previewData) ? previewData : []
  }

  getCustomCssPreview(message: SetupGuideAiMessage): string {
    if (!message.toolName) {
      return ''
    }
    return this.toolRegistry.getPreviewData(message.toolName, message) as string
  }

  getBrandingColorPreviewEntries(message: SetupGuideAiMessage): BrandingColorPreviewEntry[] {
    if (!message.toolName) {
      return []
    }
    return this.toolRegistry.getPreviewData(message.toolName, message) as BrandingColorPreviewEntry[]
  }

  getArticlePreviewEntries(message: SetupGuideAiMessage): ArticleSearchResultEntry[] {
    if (!message.toolName) {
      return []
    }
    return this.toolRegistry.getPreviewData(message.toolName, message) as ArticleSearchResultEntry[]
  }

  getArticleContentSnippet(message: SetupGuideAiMessage): string {
    if (!message.toolName) {
      return ''
    }
    const previewData = this.toolRegistry.getPreviewData(message.toolName, message) as {
      snippet: string
      url: string
      title: string
    }
    return previewData.snippet
  }

  getArticleContentUrl(message: SetupGuideAiMessage): string {
    if (!message.toolName) {
      return ''
    }
    const previewData = this.toolRegistry.getPreviewData(message.toolName, message) as {
      snippet: string
      url: string
      title: string
    }
    return previewData.url
  }

  getArticleContentTitle(message: SetupGuideAiMessage): string {
    if (!message.toolName) {
      return ''
    }
    const previewData = this.toolRegistry.getPreviewData(message.toolName, message) as {
      snippet: string
      url: string
      title: string
    }
    return previewData.title
  }

  getGeneratedImagePreview(message: SetupGuideAiMessage): string {
    if (!message.toolName) {
      return ''
    }
    return this.toolRegistry.getPreviewData(message.toolName, message) as string
  }

  onToolChipClick(tool: ToolConfig): void {
    this.selectedToolConfig = tool
    this.changeDetectorRef.markForCheck()
  }

  onClearSelectedTool(): void {
    this.selectedToolConfig = null
    this.changeDetectorRef.markForCheck()
  }

  canApplyToolResult(message: SetupGuideAiMessage): boolean {
    if (this.aiMode !== SetupGuideAiModeEnum.Act) {
      return false
    }

    if (message.type !== SetupGuideAiMessageTypeEnum.ToolResult || !message.toolName || !message.toolContent) {
      return false
    }

    return this.toolRegistry.canApply(message.toolName, message)
  }

  onContactSupportClick(event: MouseEvent): void {
    event.preventDefault()
    this.gbSetupGuideProgressService.requestLiveChatMaximize()
  }

  onApplyToolResult(message: SetupGuideAiMessage): void {
    if (!message.toolName || !message.toolContent) {
      return
    }

    const context: ToolApplyContext = {
      aiFacade: this.aiFacade,
      gbSetupGuideProgressService: this.gbSetupGuideProgressService,
    }

    this.toolRegistry.apply(message.toolName, message, context)
  }

  onEditUserMessage(message: SetupGuideAiMessage): void {
    this.editingMessageId = message.id
    this.editContentControl.reset(message.content)
    this.changeDetectorRef.markForCheck()
  }

  onCancelEdit(): void {
    this.editingMessageId = null
    this.editContentControl.reset('')
    this.changeDetectorRef.markForCheck()
  }

  onSaveEditedMessage(message: SetupGuideAiMessage): void {
    const editedContent = this.editContentControl.value?.trim()
    if (!editedContent) return

    const messageIndex = this.aiChatMessages.findIndex((msg) => msg.id === message.id)
    if (messageIndex === -1) return

    this.aiChatMessages = this.aiChatMessages.slice(0, messageIndex).concat({ ...message, content: editedContent })
    this.updateCurrentAiChatSessionMessages()

    this.editingMessageId = null
    this.editContentControl.reset('')

    const formType = this.aiChatSessionUtil.getCurrentSessionFormType()

    if (this.aiMode === SetupGuideAiModeEnum.Ask) {
      const messagesHistory = this.getAutoFillMessagesHistory(formType)
      this.processedStreamEventCount = 0
      this.aiFacade.dispatch(AiActions.galabidHelpStreamReset())
      this.aiFacade.dispatch(
        AiActions.galabidHelp({
          prompt: editedContent,
          messagesHistory,
        }),
      )
      return
    }

    if (!formType) return

    const messagesHistory = this.getAutoFillMessagesHistory(formType)
    this.processedStreamEventCount = 0
    this.aiFacade.dispatch(AiActions.autoFillFormStreamReset())
    this.aiFacade.dispatch(
      AiActions.autoFillForm({
        formType,
        prompt: editedContent,
        messagesHistory,
      }),
    )
  }

  onGenerateWithAI(formType: AutoFillFormTypeEnum | null | undefined): void {
    const rawPrompt = this.aiPromptControl.value?.trim()
    if (!rawPrompt) return

    if (
      this.aiMode === SetupGuideAiModeEnum.Ask &&
      !this.isAiModeDisabled(SetupGuideAiModeEnum.Act) &&
      this.actIntentKeywordsRegex.test(rawPrompt)
    ) {
      this.aiMode = SetupGuideAiModeEnum.Act
    }

    const selectedTool = this.selectedToolConfig
    const prompt =
      this.aiMode === SetupGuideAiModeEnum.Act && selectedTool
        ? `${this.translateService.instant('SETUP_GUIDE.AI_CHAT.USE_TOOL_PREFIX', {
            tool: this.translateService.instant(selectedTool.labelKey),
          })} ${rawPrompt}`
        : rawPrompt
    this.selectedToolConfig = null

    if (this.aiMode === SetupGuideAiModeEnum.Ask) {
      this.ensureAiChatSession(formType, prompt)
      this.appendAiChatMessage({
        id: generateChatMessageId(),
        type: SetupGuideAiMessageTypeEnum.User,
        side: 'right',
        content: prompt,
        createdAt: new Date().toISOString(),
      })
      const messagesHistory = this.getAutoFillMessagesHistory(formType)
      this.processedStreamEventCount = 0
      this.aiFacade.dispatch(AiActions.galabidHelpStreamReset())

      this.aiFacade.dispatch(
        AiActions.galabidHelp({
          prompt,
          messagesHistory,
        }),
      )

      this.resetAiPromptInput()
      return
    }

    // Below code assumes aiMode = 'act'
    if (!formType) return

    let messagesHistory: AutoFillAssistantInputItem[] | undefined

    if (formType === AutoFillFormTypeEnum.BrandingSettings) {
      this.ensureAiChatSession(formType, prompt)
      this.appendAiChatMessage({
        id: generateChatMessageId(),
        type: SetupGuideAiMessageTypeEnum.User,
        side: 'right',
        content: prompt,
        createdAt: new Date().toISOString(),
      })
      messagesHistory = this.getAutoFillMessagesHistory(formType)
      this.processedStreamEventCount = 0
      this.aiFacade.dispatch(AiActions.autoFillFormStreamReset())
    }

    this.aiFacade.dispatch(
      AiActions.autoFillForm({
        formType,
        prompt,
        messagesHistory,
        onSuccess:
          formType === AutoFillFormTypeEnum.BrandingSettings
            ? undefined
            : (result) => {
                this.gbSetupGuideProgressService.setAiFormData(formType, result as Record<string, unknown>)
              },
      }),
    )

    this.resetAiPromptInput()
  }
}
